import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@hero/web-components';
import { OrderStatusDynamicIcon } from '@hero/web-icons';
import { StatusBadgeConfigType } from '@hero/web-components';
import { getOrderStatusBadgeClass } from '@hero/web-theme';
import { Order } from '@hero/schema';

export interface OrderStatusBadgeProps {
  status: Order['status']['order'];
  statusConfig?: StatusBadgeConfigType;
  displayForUser?: 'customer' | 'carrier';
  /** Compact variant for dense surfaces like table cells. */
  size?: 'default' | 'small';
}

export const getDefaultStatusText = (
  status: Order['status']['order'],
  t: (key: string, fallback?: string) => string,
) => {
  return t(`status.${status}`, status);
};

export const OrderStatusBadge: React.FC<OrderStatusBadgeProps> = ({
  status,
  statusConfig,
  size = 'default',
}) => {
  const { t } = useTranslation('m-order');

  const statusClass = statusConfig?.getStatusClass
    ? statusConfig.getStatusClass(status)
    : getOrderStatusBadgeClass(status);

  const iconSizeClass = size === 'small' ? '!h-4 !w-4' : '!h-5 !w-5';
  const statusIcon =
    statusConfig?.showIcon && statusConfig?.getStatusIcon ? (
      statusConfig.getStatusIcon(status)
    ) : (
      <OrderStatusDynamicIcon status={status} className={iconSizeClass} />
    );

  const statusText = statusConfig?.getStatusDisplayText
    ? statusConfig.getStatusDisplayText(status)
    : t(`status.${status}`);

  return (
    <Badge variant="secondary" size={size} className={statusClass}>
      {statusIcon}
      {statusText}
    </Badge>
  );
};
